import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { aws } from '../lib/awsdb';
import { authWrite } from '../lib/authWrite';
import { useToast } from '../lib/Toast';
import { humanizeError } from '../lib/humanizeError';
import { downloadXlsx } from '../lib/exportXlsx';
import { logExportActivity } from '../lib/exportActivityLog';
import CatalogueDrawer from './CatalogueDrawer';
import SupplierPicker from './SupplierPicker';
import { Button } from '../ui/button';
import { IconDownload } from '../ui/icons';
import '../po/drawer.css';

// ── Hover marquee for cells where the text might be longer than the
//    column. Default state: nowrap + ellipsis. On hover, if the text
//    actually overflows, slide it left at a steady pace, pause briefly
//    at the end, snap back. On mouse-leave, reset immediately. Short
//    text that already fits doesn't animate at all.
//
//    Implementation: animate `text-indent` on the cell. With nowrap +
//    overflow:hidden, text-indent shifts the entire (single-line)
//    content; once shifted past the overflow distance, the right edge
//    of the content sits inside the box so the ellipsis disappears
//    naturally. The CSS variable `--marquee-end` is set per-row from
//    the measured overflow so each cell scrolls only as far as needed.
const MARQUEE_CSS = `
@keyframes fcSupplierMarquee {
  0%, 12%  { text-indent: 0; }
  72%, 88% { text-indent: var(--marquee-end, 0); }
  100%     { text-indent: 0; }
}
@media (prefers-reduced-motion: reduce) {
  [data-marquee] { animation: none !important; text-indent: 0 !important; }
}
`;

function MarqueeCell({ text }) {
  const ref = useRef(null);
  const [overflow, setOverflow] = useState(0);
  const [active, setActive] = useState(false);

  useLayoutEffect(() => {
    if (!ref.current) return;
    const measure = () => {
      const el = ref.current;
      if (!el) return;
      setOverflow(Math.max(0, el.scrollWidth - el.clientWidth));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [text]);

  const canMarquee = overflow > 2;
  // Duration: ~4s base + 12ms per overflowing px so longer text
  // travels at a roughly steady speed (not racing past).
  const durSec = canMarquee ? (4 + overflow * 0.012).toFixed(2) : 0;

  return (
    <span
      ref={ref}
      data-marquee=""
      onMouseEnter={() => canMarquee && setActive(true)}
      onMouseLeave={() => setActive(false)}
      style={{
        display: 'block',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        // CSS var read by the keyframes — overflow distance + a small
        // buffer so the last character clears the right edge cleanly.
        ['--marquee-end']: `-${overflow + 12}px`,
        animation: active ? `fcSupplierMarquee ${durSec}s linear infinite` : 'none',
      }}
    >
      {text}
    </span>
  );
}

// Packaging vocabulary — the column was previously labelled "Category"
// but actually held UoM-style values. Migration 101 renamed the column
// to `uom`. The fresh `category` column is for brand bucketing.
const UOM_ORDER = ['Sachet Boxes', 'Pouches', 'Tins', 'Bottles', 'Jars', 'Boxes', 'Rewind', 'Accessories', 'Raw Materials'];
// Brand buckets — extend as new product lines launch.
const CATEGORY_OPTIONS = ['OneFit', 'OneLife', 'OneFitUS', 'OneCreatine', 'CocoFit', 'Other'];

// ─────────────────────────────────────────────────────────────────────
// Catalogue column-sizing system
// ─────────────────────────────────────────────────────────────────────
// Table runs `table-layout: auto`, so the browser sizes each column
// to its content. Per-column intent is expressed by which BUCKET the
// column belongs to — never by hardcoded pixel widths. New columns
// pick a bucket and inherit that bucket's behaviour.
//
//   shrink   — width:1% + nowrap → tight to content. Never grows past
//              what the longest cell needs. Used for short, bounded
//              vocabularies (CATEGORY, UOM, STATUS) and for CODE,
//              which is short but must NEVER truncate — a SKU code is
//              an identifier, ellipsis breaks it.
//
//   flex     — width:auto + nowrap → absorbs whatever horizontal space
//              remains after every other column has taken its share.
//              Used for NAME, the most information-dense field. Stays
//              single-line; if it overflows the viewport, the
//              .fc-table-wrap container scrolls horizontally.
//
//   supplier — minWidth + maxWidth + nowrap + ellipsis + hover-marquee.
//              Variable-length data with a sensible upper bound. Long
//              names ellipsis-clip and slide on hover (MarqueeCell).
//
// Adding a column: classify it into one of the three buckets. Don't
// invent new widths per column — the buckets are deliberate.

const COL = {
  shrink:   { width: '1%' },
  flex:     { width: 'auto' },
  supplier: { minWidth: 220, maxWidth: 320 },
};
const NOWRAP_CELL = { whiteSpace: 'nowrap' };

function fmtDate(d) { if (!d) return '—'; return new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }); }

export default function Catalogue({ session, catalog, loadCatalog, loadSkus, skus, incomingData = [] }) {
  const toast = useToast();
  const [newSku, setNewSku] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [filter, setFilter] = useState('active'); // 'active' | 'retired' | 'all'
  const isAdmin = session?.role === 'admin';
  // toast = sleek branded popups; humanizeError translates raw DB errors
  // into plain English (e.g. "Already exists" instead of "duplicate key…").
  // Catalogue write (add/edit SKUs) is allowed for admin role OR any user
  // with the stock_write permission flag (default true; toggle off in
  // Admin Panel → user → Special). Demand approve/send stays admin-only.
  const canCatalogueWrite = isAdmin || session?.permissions?.stock_write !== false;

  const sorted = useMemo(() => {
    let items = [...catalog];
    if (filter === 'active') items = items.filter(c => c.status === 'active');
    else if (filter === 'retired') items = items.filter(c => c.status === 'retired');
    return items.sort((a, b) => {
      // Group by UoM (packaging) first, then alphabetical within group.
      const ai = UOM_ORDER.indexOf(a.uom || 'Other');
      const bi = UOM_ORDER.indexOf(b.uom || 'Other');
      if (ai !== bi) return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
      return (a.sku_name || '').localeCompare(b.sku_name || '');
    });
  }, [catalog, filter]);

  async function saveNewSku() {
    if (!newSku?.sku_name?.trim()) return;
    let catEntry;
    try {
      // authWrite returns RETURNING * array — take the first row.
      // Migration 101 split: `uom` is the packaging type, `category`
      // is the brand bucket. Both are nullable; supplier_id is the FK
      // (with supplier_name kept as a denormalised display value).
      const rows = await authWrite(session, 'forecaster_catalog', 'insert', {
        sku_name: newSku.sku_name.trim(),
        sku_code: newSku.sku_code?.trim() || null,
        supplier_id: newSku.supplier_id || null,
        supplier_name: newSku.supplier_name?.trim() || null,
        uom: newSku.uom || 'Sachet Boxes',
        category: newSku.category || null,
        status: 'active',
        unit_cost: parseFloat(newSku.unit_cost) || null,
        lead_time_days: parseInt(newSku.lead_time_days) || 84,
        batch_size: parseInt(newSku.batch_size) || 857,
        target_cover_weeks: parseInt(newSku.target_cover_weeks) || 12,
        // Migration 102 — surfaced as the synthetic "Created" entry on
        // the SKU's History tab.
        created_by: session?.name || null,
      });
      catEntry = Array.isArray(rows) ? rows[0] : null;
    } catch (catErr) {
      // 23505 on this insert means the SKU code is taken — Ace's bug.
      // Surface a clear toast and bail; don't fall through to the
      // forecaster_skus mirror insert.
      const msg = String(catErr?.message || '');
      if (/23505|duplicate key|already exists/.test(msg) && /sku_code/.test(msg)) {
        toast.error(
          'Duplicate SKU code',
          `"${newSku.sku_code?.trim()}" is already in the catalogue. Codes must be unique — pick a different one or edit the existing entry.`,
        );
        return;
      }
      const { title, body } = humanizeError(catErr, 'Could not save SKU');
      toast.error(title, body);
      return;
    }
    if (!catEntry) {
      toast.error('Could not save SKU', 'No row returned');
      return;
    }
    try {
      await authWrite(session, 'forecaster_skus', 'insert', {
        sku_name: catEntry.sku_name, catalog_id: catEntry.id,
        lead_time_days: catEntry.lead_time_days, batch_size: catEntry.batch_size,
        target_cover_weeks: catEntry.target_cover_weeks, updated_by: session.name,
      });
      toast.success('SKU added', `${catEntry.sku_name} is now in the catalogue.`);
    } catch (skuErr) {
      // 23505 = unique_violation — safe to ignore (sku row already existed
      // from a prior save or auto-sync). Anything else surfaces.
      const isDup = /duplicate key|23505|already exists/i.test(skuErr?.message || '');
      if (!isDup) {
        const { title, body } = humanizeError(skuErr, 'SKU saved but stock row failed');
        toast.error(title, body);
      } else {
        toast.success('SKU added', `${catEntry.sku_name} is now in the catalogue.`);
      }
    }
    loadCatalog?.(); loadSkus?.();
    setNewSku(null);
  }

  function handleDownload() {
    const columns = [
      { label: 'Code', width: 14, value: r => r.sku_code || '' },
      { label: 'Name', width: 32, value: r => r.sku_name || '' },
      { label: 'Category', width: 14, value: r => r.category || '' },
      { label: 'UoM', width: 14, value: r => r.uom || '' },
      { label: 'Supplier', width: 26, value: r => r.supplier_name || '' },
      { label: 'Unit Cost (AUD)', width: 14, numFmt: '"A$"#,##0.00', value: r => Number(r.unit_cost || 0) },
      { label: 'Lead Time (days)', width: 14, value: r => r.lead_time_days ?? '' },
      { label: 'Pack Size', width: 12, value: r => r.batch_size ?? '' },
      { label: 'Target Cover (wks)', width: 16, value: r => r.target_cover_weeks ?? '' },
      { label: 'Status', width: 12, value: r => r.status === 'active' ? 'Active' : 'Retired' },
    ];
    const filename = `FON_Catalogue_${new Date().toISOString().slice(0, 10)}.xlsx`;
    downloadXlsx(filename, 'Catalogue', columns, sorted);
    logExportActivity(session, 'Catalogue', filename);
  }

  // Enrich catalog item with SKU id for the detail pane
  function openDetail(item) {
    const skuRow = (skus || []).find(s => s.catalog_id === item.id);
    setSelectedItem({ ...item, _skuId: skuRow?.id || null });
  }

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 16, display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {['active', 'retired', 'all'].map(f => (
            <button key={f} className={`btn btn-sm ${filter === f ? 'btn-primary' : 'btn-secondary'}`}
              style={{ fontSize: 11, padding: '3px 10px', textTransform: 'capitalize' }}
              onClick={() => setFilter(f)}>{f}</button>
          ))}
          <span style={{ fontSize: 12, color: 'var(--text-disabled)', alignSelf: 'center', marginLeft: 4 }}>{sorted.length} SKUs</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {canCatalogueWrite && <Button kind="ghost" icon={<IconDownload />} onClick={handleDownload}>Download (Excel)</Button>}
          {canCatalogueWrite && (
            <button className="btn btn-primary btn-sm" onClick={() => setNewSku({ sku_name: '', sku_code: '', supplier_id: null, supplier_name: '', uom: 'Sachet Boxes', category: '', unit_cost: '', lead_time_days: '84', batch_size: '857', target_cover_weeks: '12' })}>+ New SKU</button>
          )}
        </div>
      </div>

      {newSku && canCatalogueWrite && (
        <div className="fc-add-panel" style={{ marginBottom: 16 }}>
          <div className="fc-add-title">Add New SKU</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 2 }}>
                <label className="form-label" style={{ fontSize: 11 }}>Name</label>
                <input className="form-input" placeholder="e.g. OneFit Berry Blast Pouch" value={newSku.sku_name} onChange={e => setNewSku(f => ({ ...f, sku_name: e.target.value }))} autoFocus />
              </div>
              <div style={{ flex: 1 }}>
                <label className="form-label" style={{ fontSize: 11 }}>Code</label>
                <input className="form-input" placeholder="e.g. OF-BB-PCH-700" value={newSku.sku_code} onChange={e => setNewSku(f => ({ ...f, sku_code: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label className="form-label" style={{ fontSize: 11 }}>Supplier</label>
                <SupplierPicker
                  session={session}
                  value={{ id: newSku.supplier_id || null, name: newSku.supplier_name || '' }}
                  onChange={({ id, name }) => setNewSku(f => ({ ...f, supplier_id: id, supplier_name: name }))}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label className="form-label" style={{ fontSize: 11 }}>UoM / Packaging</label>
                <select className="form-select" value={newSku.uom} onChange={e => setNewSku(f => ({ ...f, uom: e.target.value }))}>
                  {UOM_ORDER.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label className="form-label" style={{ fontSize: 11 }}>Category (brand)</label>
                <select className="form-select" value={newSku.category || ''} onChange={e => setNewSku(f => ({ ...f, category: e.target.value }))}>
                  <option value="">— Pick a brand —</option>
                  {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label className="form-label" style={{ fontSize: 11 }}>Unit Cost</label>
                <input className="form-input" type="number" step="0.01" placeholder="0.00" value={newSku.unit_cost} onChange={e => setNewSku(f => ({ ...f, unit_cost: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label className="form-label" style={{ fontSize: 11 }}>Lead Time (days)</label>
                <input className="form-input" type="number" value={newSku.lead_time_days} onChange={e => setNewSku(f => ({ ...f, lead_time_days: e.target.value }))} />
              </div>
              <div style={{ flex: 1 }}>
                <label className="form-label" style={{ fontSize: 11 }}>Pack Size</label>
                <input className="form-input" type="number" value={newSku.batch_size} onChange={e => setNewSku(f => ({ ...f, batch_size: e.target.value }))} />
              </div>
              <div style={{ flex: 1 }}>
                <label className="form-label" style={{ fontSize: 11 }}>Target Cover (weeks)</label>
                <input className="form-input" type="number" value={newSku.target_cover_weeks} onChange={e => setNewSku(f => ({ ...f, target_cover_weeks: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button className="btn btn-primary btn-sm" onClick={saveNewSku}>Add SKU</button>
              <button className="btn btn-secondary btn-sm" onClick={() => setNewSku(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <style>{MARQUEE_CSS}</style>
      <div className="fc-table-wrap">
        <table className="fc-table" style={{ tableLayout: 'auto', width: '100%' }}>
          <thead>
            <tr>
              {/* Width rebalance (Ace audit follow-up): Name was absorbing
                  all the slack on wide monitors, pinning Category/UoM/
                  Supplier/Status to the right edge. Explicit widths on
                  every column (and a max-width on Name) keep the right-
                  side fields breathing instead of squished. */}
              {/* See COL bucket docblock above the file's constants. */}
              <th className="fc-th" style={{ ...COL.shrink, ...NOWRAP_CELL }}>Code</th>
              <th className="fc-th" style={{ ...COL.flex,   ...NOWRAP_CELL }}>Name</th>
              <th className="fc-th" style={{ ...COL.shrink, ...NOWRAP_CELL }}>Category</th>
              <th className="fc-th" style={{ ...COL.shrink, ...NOWRAP_CELL }}>UoM</th>
              <th className="fc-th" style={COL.supplier}>Supplier</th>
              <th className="fc-th" style={{ ...COL.shrink, ...NOWRAP_CELL }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(item => {
              return (
                <tr key={item.id} className="fc-row-hover fc-clickable" onClick={() => openDetail(item)}
                  style={item.status === 'retired' ? { opacity: 0.5 } : undefined}>
                  {/* CODE + NAME are info-heavy and must never truncate.
                      Both are nowrap; they push the table wider if
                      needed and .fc-table-wrap scrolls. */}
                  <td className="fc-td fc-code" style={NOWRAP_CELL}>{item.sku_code || '—'}</td>
                  <td className="fc-td fc-name" style={NOWRAP_CELL}>{item.sku_name}</td>
                  {/* Short bounded vocabularies — tight to content. */}
                  <td className="fc-td" style={{ ...NOWRAP_CELL, fontSize: 12 }}>{item.category || '—'}</td>
                  <td className="fc-td" style={{ ...NOWRAP_CELL, fontSize: 12 }}>{item.uom || '—'}</td>
                  {/* SUPPLIER — variable length, capped, ellipsis +
                      marquee on hover when overflowing. Outer td has
                      no padding so the marquee container sits flush
                      to the column edges and the ellipsis lines up
                      with the right border. */}
                  <td className="fc-td" style={{ ...COL.supplier, fontSize: 12, padding: 0, overflow: 'hidden' }}>
                    <div style={{ padding: '10px 16px' }}>
                      <MarqueeCell text={item.supplier_name || '—'} />
                    </div>
                  </td>
                  <td className="fc-td" style={NOWRAP_CELL}>
                    <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 999, background: item.status === 'active' ? 'rgba(22,163,74,0.1)' : 'rgba(0,0,0,0.05)', color: item.status === 'active' ? '#16a34a' : '#9ca3af' }}>
                      {item.status === 'active' ? 'Active' : 'Retired'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {sorted.length === 0 && <div className="fc-empty" style={{ padding: 48 }}>No SKUs match this filter.</div>}

      {/* New CatalogueDrawer (replaces SkuDetailPane for this context) */}
      {selectedItem && (
        <CatalogueDrawer
          session={session}
          item={selectedItem}
          allSkus={skus || []}
          incomingData={incomingData}
          onClose={() => setSelectedItem(null)}
          onSaved={() => { loadCatalog?.(); loadSkus?.(); }}
        />
      )}
    </>
  );
}
